import { type NotificationItem, SWAP_STAGES, type SwapFlowId, type SwapStage, type UnitUsdcSwapPhase } from '@/types/notification.types';

const SWAP_FLOW_IDS = new Set<SwapFlowId>([
  'unit_to_usdc',
  'unit_to_wunit',
  'wunit_to_unit',
  'wunit_to_usdc',
  'usdc_to_wunit',
  'usdc_to_unit',
]);

const TERMINAL_UNIT_USDC_SWAP_PHASES = new Set<UnitUsdcSwapPhase>(['completed', 'failed', 'skipped']);

export function isSwapDrawerFlow(swapFlow: string | undefined): swapFlow is SwapFlowId {
  return Boolean(swapFlow && SWAP_FLOW_IDS.has(swapFlow as SwapFlowId));
}

export function isUnitUsdcSwapProgressNotification(item: NotificationItem): boolean {
  return item.type === 'progress' && isSwapDrawerFlow(item.metadata?.swapFlow);
}

export function isPendingUnitUsdcSwapNotification(item: NotificationItem): boolean {
  const phase = item.metadata?.swapPhase;
  if (!isSwapDrawerFlow(item.metadata?.swapFlow) || !phase) return false;
  return !TERMINAL_UNIT_USDC_SWAP_PHASES.has(phase);
}

/** Vault-blocking in-progress NC items (excludes SwapDrawer swap progress). */
export function hasPendingVaultAction(notifications: NotificationItem[]): boolean {
  return notifications.some((item) => item.type === 'progress' && !isUnitUsdcSwapProgressNotification(item));
}

/** Unfinished SwapDrawer flow tracked in NC. */
export function hasPendingSwapAction(notifications: NotificationItem[]): boolean {
  return notifications.some(isPendingUnitUsdcSwapNotification);
}

const SIGNED_UNWRAP_LINK_LABELS = new Set(['wUNIT burn', 'UNIT release']);
const SWAP_STAGE_SET = new Set<string>(SWAP_STAGES);

export function isSwapStage(value: unknown): value is SwapStage {
  return typeof value === 'string' && SWAP_STAGE_SET.has(value);
}

/** Stored stage, or the release leg inferred from a burn/release link on an item saved before `swapStage`. */
export function readStoredSwapStage(
  item: { metadata?: NotificationItem['metadata']; actionLinks?: { label: string }[] } | null | undefined,
): SwapStage | undefined {
  const stage = item?.metadata?.swapStage;
  if (isSwapStage(stage)) return stage;
  const flow = item?.metadata?.swapFlow;
  const legacy = item?.metadata?.swapStep;
  const pastUnwrap =
    legacy === 'unit_release' ||
    legacy === 'complete' ||
    Boolean(item?.actionLinks?.some((link) => SIGNED_UNWRAP_LINK_LABELS.has(link.label)));
  if (!pastUnwrap) return undefined;
  if (flow === 'wunit_to_unit') return '2_unit_release_confirming';
  if (flow === 'usdc_to_unit') return '4_unit_release_confirming';
  return undefined;
}

/** Continue is the second signature only, and never after that leg has moved on or the phase is failed or skipped. */
export function swapNotificationShowsContinue(
  item: { metadata?: NotificationItem['metadata']; actionLinks?: { label: string }[] } | null | undefined,
): boolean {
  const phase = item?.metadata?.swapPhase;
  if (phase === 'failed' || phase === 'skipped' || phase === 'completed') return false;
  const stage = readStoredSwapStage(item);
  if (stage) return stage.includes('signature_required') && phase === 'awaiting_evm_sign';
  return phase === 'awaiting_evm_sign';
}

/** User must sign / Continue (toolbar attention indicator). */
export function hasSwapUserActionRequired(notifications: NotificationItem[]): boolean {
  return notifications.some((item) => isSwapDrawerFlow(item.metadata?.swapFlow) && swapNotificationShowsContinue(item));
}

/**
 * Decide whether to KEEP the currently-running pending-transaction poll rather
 * than aborting and restarting it.
 *
 * `setPendingTransaction` is re-invoked on every wallet reconnect (via
 * `initialize`'s resume of a persisted "In Progress" tx). It previously aborted
 * and restarted the poll each time, which orphaned the in-flight poll — so a
 * confirmation was never observed and the UI stayed locked until a page refresh.
 * Keep the live poll when it is already tracking the same tx; otherwise (a
 * different tx, or no live poll) (re)start it.
 */
export function shouldKeepActivePoll(params: { incomingTxid: string; activePollTxid: string | null; isActivePollLive: boolean }): boolean {
  return params.isActivePollLive && params.activePollTxid === params.incomingTxid;
}
